export const DEFAULT_INSULIN_AGE_MODE = 'adult'

export const LOOP_INSULIN_AGE_MODES = Object.freeze([
  {
    name: '成人模式',
    label: '普通速效峰值 75 分钟',
    value: 'adult'
  },
  {
    name: '儿童模式',
    label: '普通速效峰值 65 分钟',
    value: 'child'
  }
])

export const LOOP_INSULIN_TYPES = Object.freeze([
  {
    name: '门冬胰岛素(Novolog)',
    label: '普通速效曲线',
    value: '1',
    preset: 'rapidActing'
  },
  {
    name: '赖脯胰岛素(Humalog)',
    label: '普通速效曲线',
    value: '2',
    preset: 'rapidActing'
  },
  {
    name: '谷赖胰岛素(Apidra)',
    label: '普通速效曲线',
    value: '3',
    preset: 'rapidActing'
  },
  {
    name: '餐时门冬胰岛素(Fiasp)',
    label: '超速效曲线，峰值 55 分钟',
    value: '4',
    preset: 'fiasp'
  },
  {
    name: '餐时赖脯胰岛素(Lyumjev)',
    label: '超速效曲线，峰值 55 分钟',
    value: '5',
    preset: 'lyumjev'
  }
])

export const SHORT_ACTING_INSULIN_TYPES = Object.freeze([
  {
    name: '诺和灵R（生物合成人胰岛素）',
    label: '短效人胰岛素',
    value: '6',
    preset: 'regular',
    peakAfterInjectionMinutes: 210
  },
  {
    name: '优泌林R（重组人胰岛素）',
    label: '短效人胰岛素',
    value: '7',
    preset: 'regular',
    peakAfterInjectionMinutes: 180
  },
  {
    name: '甘舒霖R（重组人胰岛素）',
    label: '短效人胰岛素',
    value: '8',
    preset: 'regular',
    peakAfterInjectionMinutes: 180
  }
])

export const WZB_INSULIN_CATEGORIES = Object.freeze([
  {
    name: '速效胰岛素',
    value: 'rapid',
    types: LOOP_INSULIN_TYPES
  },
  {
    name: '短效胰岛素',
    value: 'short',
    types: SHORT_ACTING_INSULIN_TYPES
  }
])

const ACTION_DURATION_MINUTES = 360
const DELAY_MINUTES = 10
const MAX_PEAK_FRACTION = 0.49
const MIN_EFFECT_DURATION_MINUTES = 180
const MAX_EFFECT_DURATION_MINUTES = 720
const MIN_FORMULA_ACTION_DURATION_MINUTES = 3

// Regular (short-acting) insulin uses the same exponential model with its own
// timing preset. Product configuration stores the peak time from injection;
// the formula peak is measured after the delay and is converted below.
const REGULAR_ACTION_DURATION_MINUTES = 450
const REGULAR_DELAY_MINUTES = 30
const REGULAR_DEFAULT_PEAK_AFTER_INJECTION_MINUTES = 180

const ALL_INSULIN_TYPES = [...LOOP_INSULIN_TYPES, ...SHORT_ACTING_INSULIN_TYPES]

const findInsulinType = insulinType =>
  ALL_INSULIN_TYPES.find(item => item.value === String(insulinType || ''))

const isAgeMode = ageMode => LOOP_INSULIN_AGE_MODES.some(item => item.value === ageMode)

export function getInsulinTypeName(insulinType) {
  const type = findInsulinType(insulinType)
  return type ? type.name : '--'
}

export function getInsulinAgeModeName(ageMode) {
  const mode = LOOP_INSULIN_AGE_MODES.find(item => item.value === ageMode)
  return mode ? mode.name : '--'
}

export function getLoopInsulinPreset(insulinType, ageMode = DEFAULT_INSULIN_AGE_MODE) {
  const type = findInsulinType(insulinType) || LOOP_INSULIN_TYPES[0]
  const normalizedAgeMode = isAgeMode(ageMode) ? ageMode : DEFAULT_INSULIN_AGE_MODE
  const isUltraRapid = type.preset === 'fiasp' || type.preset === 'lyumjev'

  if (type.preset === 'regular') {
    const peakAfterInjectionMinutes =
      type.peakAfterInjectionMinutes || REGULAR_DEFAULT_PEAK_AFTER_INJECTION_MINUTES

    return {
      actionDurationMinutes: REGULAR_ACTION_DURATION_MINUTES,
      peakActivityMinutes: peakAfterInjectionMinutes - REGULAR_DELAY_MINUTES,
      peakAfterInjectionMinutes,
      delayMinutes: REGULAR_DELAY_MINUTES,
      effectDurationMinutes: REGULAR_ACTION_DURATION_MINUTES + REGULAR_DELAY_MINUTES
    }
  }

  return {
    actionDurationMinutes: ACTION_DURATION_MINUTES,
    peakActivityMinutes: isUltraRapid ? 55 : normalizedAgeMode === 'child' ? 65 : 75,
    delayMinutes: DELAY_MINUTES,
    effectDurationMinutes: ACTION_DURATION_MINUTES + DELAY_MINUTES
  }
}

const toFiniteNumber = value => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || !value.trim()) return null
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const normalizeEffectDuration = (value, fallback) => {
  const numericValue = toFiniteNumber(value)
  if (numericValue == null || numericValue <= 0) return fallback
  // 兼容早期配置中以小时保存的 activeTime/activTime。
  const duration = numericValue <= 12 ? numericValue * 60 : numericValue
  return duration >= MIN_EFFECT_DURATION_MINUTES && duration <= MAX_EFFECT_DURATION_MINUTES
    ? duration
    : fallback
}

/**
 * 生成实际参与 Loop 公式的参数。
 *
 * recordConfig 中的时间语义：
 * - activeTime：从注射开始到作用结束的总分钟数（包含 delayTime）
 * - peakTime：从注射开始到峰值的分钟数
 * - delayTime：注射后的延迟起效分钟数
 *
 * 单条记录有快照时优先使用快照；旧记录缺字段时回退到当前类型预设。
 */
export function getInsulinActionConfig(insulinType, ageMode = DEFAULT_INSULIN_AGE_MODE, recordConfig = {}) {
  const config = recordConfig && typeof recordConfig === 'object' ? recordConfig : {}
  const fallbackInsulinType = findInsulinType(insulinType) ? String(insulinType) : LOOP_INSULIN_TYPES[0].value
  const configuredInsulinType = findInsulinType(config.insulinType)
  const resolvedInsulinType = configuredInsulinType ? configuredInsulinType.value : fallbackInsulinType
  const resolvedAgeMode = isAgeMode(config.ageMode) ? config.ageMode : ageMode
  const preset = getLoopInsulinPreset(resolvedInsulinType, resolvedAgeMode)

  const effectDurationMinutes = normalizeEffectDuration(config.activeTime, preset.effectDurationMinutes)
  const configuredDelay = toFiniteNumber(config.delayTime)
  const delayMinutes =
    configuredDelay != null &&
    configuredDelay >= 0 &&
    effectDurationMinutes - configuredDelay >= MIN_FORMULA_ACTION_DURATION_MINUTES
      ? configuredDelay
      : preset.delayMinutes

  const actionDurationMinutes = effectDurationMinutes - delayMinutes
  const presetPeakAfterInjection =
    preset.peakAfterInjectionMinutes || preset.peakActivityMinutes + preset.delayMinutes
  const configuredPeak = toFiniteNumber(config.peakTime)
  const configuredPeakAfterInjection =
    configuredPeak != null && configuredPeak > delayMinutes
      ? configuredPeak
      : presetPeakAfterInjection

  // Loop 指数模型要求公式峰值位于作用时长前半段。自定义时长可能
  // 把原峰值压到临界点或临界点之后，这里只在不合法时收敛到 49%，
  // 防止 tau 除零、负数及最终 IOB 出现 NaN。
  const peakActivityMinutes = Math.max(
    1,
    Math.min(configuredPeakAfterInjection - delayMinutes, actionDurationMinutes * MAX_PEAK_FRACTION)
  )

  return {
    insulinType: resolvedInsulinType,
    ageMode: isAgeMode(resolvedAgeMode) ? resolvedAgeMode : DEFAULT_INSULIN_AGE_MODE,
    actionDurationMinutes,
    effectDurationMinutes,
    peakActivityMinutes,
    peakAfterInjectionMinutes: peakActivityMinutes + delayMinutes,
    delayMinutes,
    customTime: Boolean(config.customTime)
  }
}

export function getInsulinEffectDurationHours(insulinType, ageMode, recordConfig) {
  return getInsulinActionConfig(insulinType, ageMode, recordConfig).effectDurationMinutes / 60
}

export function getRecordInsulinEffectDurationHours(
  record,
  fallbackInsulinType,
  fallbackAgeMode = DEFAULT_INSULIN_AGE_MODE
) {
  return getInsulinEffectDurationHours(fallbackInsulinType, fallbackAgeMode, record)
}

export function getInsulinRecordEffectEndTimestamp(
  record,
  fallbackInsulinType,
  fallbackAgeMode = DEFAULT_INSULIN_AGE_MODE
) {
  const injectionTimestamp = new Date(String((record && record.createTime) || '').replace(/-/g, '/')).getTime()
  if (!Number.isFinite(injectionTimestamp)) return 0
  return (
    injectionTimestamp +
    getRecordInsulinEffectDurationHours(record, fallbackInsulinType, fallbackAgeMode) * 60 * 60 * 1000
  )
}

export function getLatestInsulinEffectEndTimestamp(
  records,
  fallbackInsulinType,
  fallbackAgeMode = DEFAULT_INSULIN_AGE_MODE
) {
  return (Array.isArray(records) ? records : []).reduce((latest, record) => {
    return Math.max(
      latest,
      getInsulinRecordEffectEndTimestamp(record, fallbackInsulinType, fallbackAgeMode)
    )
  }, 0)
}

// Returns the fraction (0...1) of insulin effect remaining after a dose.
export function percentEffectRemaining(elapsedMinutes, insulinType, ageMode, recordConfig) {
  const elapsed = Number(elapsedMinutes)
  if (!Number.isFinite(elapsed)) return 0

  const {
    actionDurationMinutes: actionDuration,
    peakActivityMinutes: peakActivity,
    delayMinutes: delay
  } = getInsulinActionConfig(insulinType, ageMode, recordConfig)
  const timeAfterDelay = elapsed - delay

  if (timeAfterDelay <= 0) return 1
  if (timeAfterDelay >= actionDuration) return 0

  const tau =
    (peakActivity * (1 - peakActivity / actionDuration)) /
    (1 - (2 * peakActivity) / actionDuration)
  const a = (2 * tau) / actionDuration
  const scale = 1 / (1 - a + (1 + a) * Math.exp(-actionDuration / tau))
  const t = timeAfterDelay

  // 与原 Loop 公式代数等价，但消除了 a=1 时 (1-a) * Infinity
  // 的不稳定中间值。
  const remaining =
    1 -
    scale *
      ((t * t) / (tau * actionDuration) - (1 - a) * (t / tau + 1)) *
      Math.exp(-t / tau) -
    scale * (1 - a)

  if (!Number.isFinite(remaining)) return 0
  return Math.min(1, Math.max(0, remaining))
}

export function percentEffectRemainingForRecord(
  elapsedMinutes,
  record,
  fallbackInsulinType,
  fallbackAgeMode = DEFAULT_INSULIN_AGE_MODE
) {
  return percentEffectRemaining(elapsedMinutes, fallbackInsulinType, fallbackAgeMode, record)
}

export function calculateDoseIob(doseUnits, elapsedMinutes, insulinType, ageMode, recordConfig) {
  const dose = Number(doseUnits)
  if (!Number.isFinite(dose) || dose <= 0) return 0
  return dose * percentEffectRemaining(elapsedMinutes, insulinType, ageMode, recordConfig)
}

export function calculateRecordIob(
  record,
  elapsedMinutes,
  fallbackInsulinType,
  fallbackAgeMode = DEFAULT_INSULIN_AGE_MODE
) {
  return calculateDoseIob(
    record && record.realInsulin,
    elapsedMinutes,
    fallbackInsulinType,
    fallbackAgeMode,
    record
  )
}
